import React, { useCallback, useEffect, useRef, useState } from 'react';
import './Navbar.css';
import Logo from '../Logo/Logo';
import Swal from 'sweetalert2';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';

const Navbar = ({ darkmodeClass, isDarkMode, setDarkMode, clearFilters, setAuthenticated }) => {
  const auth = getAuth();
  const navigate = useNavigate();
  const currentLocation = useLocation();
  const navbarRef = useRef(null);
  const userContainerRef = useRef(null);
  const signOutButtonRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [redirectToSignIn, setRedirectToSignIn] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [showUserInfo, setShowUserInfo] = useState(false);

  const handleSignInClick = () => {
    setRedirectToSignIn(false);
    navigate('/signin');
  };
  
  const handleSignOutConfirm = async () => {
    try {
      if (showUserInfo) {
        // Perform sign-out only when user info is displayed
        await signOut(auth);
        setAuthenticated(false);
        setUserDetails(null);
        setShowUserInfo(false); // Close user info on sign-out
        setRedirectToSignIn(true); // Redirect to sign-in page
        console.log("Sign out successful");
      } else {
        console.warn("User info not displayed. Sign-out aborted.");
      }
    } catch (error) {
      console.error('Error signing out:', error.message);
    }
  };

  const handleUserMouseEnter = () => {
    // Cancel the hiding of user info when the mouse enters the user profile area
    clearTimeout(hideUserInfoTimeout);
    setShowUserInfo(true); // Ensure user info is visible when entering
  };
  
  const handleUserMouseLeave = () => {
    // Delay the hiding of user info to allow moving to other elements
    hideUserInfoTimeout = setTimeout(() => {
      setShowUserInfo(false);
    }, 500); // Adjust the delay (in milliseconds) as needed
  };
  
  const handleSignOutMouseEnter = () => {
    // Cancel the hiding of user info if the sign-out button is hovered
    clearTimeout(hideUserInfoTimeout);
  };
  
  let hideUserInfoTimeout;
  const handleSignOutMouseLeave = () => {
    // Delay the hiding of user info to allow moving to other elements
    hideUserInfoTimeout = setTimeout(() => {
      setShowUserInfo(false);
    }, 500); // Adjust the delay (in milliseconds) as needed
  };

  const handleUserClick = (event) => {
    event.stopPropagation();
    setShowUserInfo(!showUserInfo);
  };

 
  const handleMenuToggle = useCallback(() => {
    if (!isConfirmationModalOpen) {
      setMenuOpen(!menuOpen);
    }
  }, [isConfirmationModalOpen, menuOpen]);



 const handleToggleDarkMode = () => {
  Swal.fire({
    title: `Do you want to open ${isDarkMode ? 'light' : 'dark'} mode?`,
    customClass: {
      icon: `toggle fa-solid ${isDarkMode ? 'fa-sun' : 'fa-moon'}`,
    },
    showCancelButton: true,
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    allowOutsideClick: false,
    showCloseButton: true,
    confirmButtonColor: '#28a745',
    cancelButtonColor: '#dc3545',
  }).then((result) => {
    if (result.isConfirmed) {
      setDarkMode(!isDarkMode);
      Swal.fire({
        title: `${isDarkMode ? 'Light' : 'Dark'} mode enabled!`,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      });
    } else {
      Swal.fire({
        title: `${isDarkMode ? 'Light' : 'Dark'} mode not enabled`,
        icon: 'info',
        timer: 2000,
        showConfirmButton: false,
      });
    }
  });
};


  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log("User details:", user);
      if (user) {
        setUserDetails(user);
        setAuthenticated(true);
      } else {
        setUserDetails(null);
        setAuthenticated(false);
      }
    });
  
    return () => unsubscribe();
  }, [auth, setAuthenticated]);
  

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!isAuthenticated && !redirectToSignIn) {
        setRedirectToSignIn(true);
        navigate('/signin');
      }
    }, 3000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isAuthenticated, redirectToSignIn, navigate]);

  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };

    if (isConfirmationModalOpen || menuOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      window.addEventListener('scroll', handleScroll);
    } else {
      document.body.style.overflow = 'auto';
      document.documentElement.style.overflow = 'auto';
      window.removeEventListener('scroll', handleScroll);
      window.scrollTo(0, scrollPosition);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isConfirmationModalOpen, menuOpen, scrollPosition]);

  useEffect(() => {
    document.body.style.overflow = menuOpen || isConfirmationModalOpen ? 'hidden' : 'auto';
    document.documentElement.style.overflow = menuOpen || isConfirmationModalOpen ? 'hidden' : 'auto';
    window.scrollTo(0, scrollPosition);
  }, [menuOpen, isConfirmationModalOpen, scrollPosition, isAuthenticated]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        navbarRef.current &&
        !navbarRef.current.contains(event.target) &&
        event.target.className !== 'logo navbar '
      ) {
        setMenuOpen();
      }
    };

    document.body.addEventListener('click', handleClickOutside);

    return () => {
      document.body.removeEventListener('click', handleClickOutside);
    };
  }, [handleMenuToggle]);


  return (
    <nav ref={navbarRef} className={`navbar ${darkmodeClass}`}>
      <label htmlFor="check" className={`checked ${darkmodeClass}`} onClick={handleMenuToggle}>
        {menuOpen ? <i className="fa-solid fa-times active"></i> : <i className="fa-solid fa-bars"></i>}
      </label>
      <div className='nav1'>
      <div className="logo-container">
        <Link to="/" className={`logo ${darkmodeClass}`}>
        <Logo clearFilters={clearFilters} setMenuOpen={setMenuOpen} darkmodeClass={darkmodeClass} />
        </Link>
      </div>
      </div>
     <div className='nav2'>
     <ul className={`nav-list ${darkmodeClass} ${menuOpen ? 'menu-open active' : ''}`} onClick={handleMenuToggle}>
      <li className={`${darkmodeClass} ${currentLocation.pathname === '/' ? 'active' : ''}`}>
        <Link to="/" className={`link ${darkmodeClass}`} onClick={handleMenuToggle}><i class="fa-solid fa-house"></i>
          Home
        </Link>
      </li>
      <li className={`${darkmodeClass} ${currentLocation.pathname === '/about' ? 'active' : ''}`}>
        <Link to="/about" className={`link ${darkmodeClass}`} onClick={handleMenuToggle}><i class="fa-solid fa-file-lines"></i>
          About
        </Link>
      </li>
      <li className={`${darkmodeClass} ${currentLocation.pathname === '/contact' ? 'active' : ''}`}>
        <Link to="/contact" className={`link ${darkmodeClass}`} onClick={handleMenuToggle}><i class="fa-solid fa-address-book"></i>
          Contact
        </Link>
      </li>
      <li className={`${darkmodeClass} ${currentLocation.pathname === '/services' ? 'active' : ''}`}>
        <Link to="/services" className={`link ${darkmodeClass}`} onClick={handleMenuToggle}><i class="fa-solid fa-user-gear"></i>
          Services
        </Link>
      </li>
    </ul>
    
    <div className="dark-mode-toggle">
        <label className="switch">
          <input type="checkbox" checked={isDarkMode} onChange={handleToggleDarkMode} />
          <span>
          <i className={`toggle fa-solid ${isDarkMode ? 'fa-sun' : 'fa-moon'}`}></i>
          </span>
        </label>
       <div className='user'>
       <div>
       <label>
          {userDetails ? (
            <div
              className="user-profile"
              ref={userContainerRef}
              onMouseEnter={handleUserMouseEnter}
              onMouseLeave={handleUserMouseLeave}
              onClick={handleUserClick}

            >
              <div className="user-profile-pic-container">
                {userDetails.photoURL ? (
                  <img src={userDetails.photoURL} alt="User Profile" className="user-profile-pic" />
                ) : (
                  <div className='fallback-profile-pic-container'>
                    <div className="fallback-profile-pic">
                    {userDetails.displayName ? (
                      userDetails.displayName
                        .split(" ")
                        .map((namePart, index) => (index < 2 ? namePart.charAt(0) : ""))
                        .join("")
                    ) : userDetails.email ? (
                      userDetails.email.charAt(0).toUpperCase()
                    ) : (
                      "A" // Fallback if neither displayName nor email is available
                    )}
                  </div>
                    </div>
                )}
              </div>

              {userDetails && showUserInfo && (
                <div className={`${darkmodeClass} user-info`}>
                  <div className="user-info-img">
                    {userDetails.photoURL ? (
                      <img src={userDetails.photoURL} alt="User Profile" className="user-profile-pic" />
                    ) : (
                      <div className='fallback-info-img'>
                        <div className="fallback-profile-pic">
                        {userDetails.displayName ? (
                          userDetails.displayName
                            .split(" ")
                            .map((namePart, index) => (index < 2 ? namePart.charAt(0) : ""))
                            .join("")
                        ) : userDetails.email ? (
                          userDetails.email.charAt(0).toUpperCase()
                        ) : (
                          "A"
                        )}
                      </div>
                        </div>
                    )}
                  </div>
                  <h4>{userDetails.displayName}</h4>
                  <p>{userDetails.email}</p>
                  </div>
              )}
            </div>
          ) : (
            <button className="signin"  onClick={handleSignInClick}><i class="fa-regular fa-user"></i></button>
          )}


        </label>
       </div>

       <div className='signout-div'>
                    {userDetails && showUserInfo && (
                      <button
                      className={`${darkmodeClass} signout`}
                      ref={signOutButtonRef}
                      onClick={handleSignOutConfirm}
                      onMouseEnter={handleSignOutMouseEnter}
                      onMouseLeave={handleSignOutMouseLeave}
                      >
                        <i className="fa-solid fa-right-from-bracket"></i>Sign Out
                      </button>
                    )}
        </div>
       </div>
      </div>
     </div>
    </nav>
  );
};

export default Navbar;